import { useState } from "react";
import { useAnswers } from "../funnel/answers";
import { draftDays, useDraft } from "../funnel/draft";
import { longDay } from "../funnel/dates";
import { SaveNote, useStepSave } from "../funnel/stepSave";
import { useFunnelNav } from "../funnel/useFunnelNav";
import { useParticipation } from "../funnel/useParticipation";
import { fallbackOf } from "../settings/catalog";
import { useLocale, useSettings, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * L'ÉTAPE DATES — quels jours, sur un événement qui en a plusieurs.
 *
 * ⚠️ ELLE NE FAIT QUE COCHER : rien n'est écrit ici. Les jours partent avec le reste
 * de l'inscription, à la confirmation, et pour une raison précise — les écrire au
 * passage inscrirait pour de bon quelqu'un qui quitte le tunnel à l'étape suivante,
 * là où toutes les autres réponses de ce parcours attendent d'être confirmées. C'est
 * aussi ce que fait la V1, qui ne les envoie qu'à son écran caché juste avant la
 * confirmation.
 *
 * ⚠️ MAIS ILS SONT GARDÉS EN BROUILLON DANS CE NAVIGATEUR, sans rien inscrire : qui
 * abandonne en route les retrouve cochés à son retour. Voir `funnel/draft.ts`.
 *
 * ⚠️ SAUF SERVIE EN PAGE (`asPage`), hors du tunnel : il n'y a pas de confirmation où
 * aller, et « Enregistrer » écrit ici même. Dans le tunnel, même pour corriger une
 * inscription enregistrée, la correction suit le parcours jusqu'à la confirmation
 * (tâche #546, voir la rangée d'actions plus bas).
 *
 * ⚠️ ET SON ABSENCE VEUT DIRE « TOUS LES JOURS », PAS « AUCUN ». Un événement d'un
 * seul jour, ou dont l'organisateur a retiré l'étape, inscrit à tout — voir
 * `funnel/dates.ts`, où la règle est écrite et testée.
 *
 * Displayed strings are French: this is a French product.
 */

const ICI = "/registration/dates";

export function Dates({ asPage }: { asPage?: true }) {
  const t = useT();
  const { days } = useSettings();

  const title = useText(
    "FO_MEETING_V2_DATES_TITLE",
    fallbackOf("FO_MEETING_V2_DATES_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_DATES_SUBTITLE",
    fallbackOf("FO_MEETING_V2_DATES_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_DATES_HTML");

  const [answers, record] = useAnswers();
  const { me, recorded } = useParticipation();
  /* Les jours cochés lors d'un premier parcours abandonné, entre ce parcours-ci et
     l'enregistré (voir `funnel/draft.ts`). */
  const { draft, keep } = useDraft(me, recorded);

  /**
   * ⚠️ UN ÉTAT LOCAL DEVANT LES DEUX AUTRES SOURCES : `readAnswers` écartait une liste
   * VIDE, donc tout décocher retombait sur les jours enregistrés — et les cases se
   * recochaient toutes seules sous la main du participant. Il la garde désormais
   * (bug #257), mais le `null` reste ce qui distingue « pas encore touché » de
   * « touché, et vide » sans attendre que `record` ait navigué.
   */
  const [picked, setPicked] = useState<number[] | null>(null);
  const ticked =
    picked ??
    answers.days ??
    draftDays(
      draft,
      days.map((d) => d.id),
    ) ??
    recorded?.days ??
    [];

  /* La langue du participant, telle que la base la nomme (« FR », « EN »). */
  const locale = useLocale();
  const { previous, next, go } = useFunnelNav(ICI);
  const { ready, state, save, touched, removal } = useStepSave({ asPage });

  const bascule = (id: number) => {
    const next = ticked.includes(id)
      ? ticked.filter((d) => d !== id)
      : [...ticked, id];
    setPicked(next);
    // « C'est enregistré » ne doit pas rester affiché au-dessus d'une case qu'on vient
    // de décocher depuis.
    touched();
    // Enregistré dans CETTE entrée d'historique, comme les autres choix du tunnel :
    // sans quoi le bouton Retour du navigateur rendrait l'étape vierge.
    record({ days: next });
    keep({ days: next });
  };

  return (
    <section className="page">
      <div className="ttl" data-cfg="datesTitle">
        <InPlace settingKey="FO_MEETING_V2_DATES_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_DATES_SUBTITLE" text={subtitle} as="p" />
      </div>

      {html.trim() !== "" && (
        <Prose
          html={html}
          className="se-prose"
          data-cfg="datesTitle"
          />
      )}

      {/* ⚠️ DIT PLUTÔT QUE LAISSÉ VIDE, et adressé à l'ORGANISATEUR : un participant
          n'arrive jamais ici sur un événement d'un seul jour, le fil ne l'y mène pas.
          Celui qui y arrive est en train de configurer l'étape, et une liste d'un
          élément le laisserait croire à une panne. */}
      <div className="alert" hidden={days.length > 1}>
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>Moins de deux jours</b>
          <small>
            Cet événement ne laisse rien à choisir : l'étape n'est pas servie, et
            chacun est inscrit à tous ses jours. Les jours se déclarent dans le back
            office.
          </small>
        </span>
      </div>

      {/* ⚠️ ANCRÉE SUR LA FAMILLE DE L'ÉTAPE, faute d'en avoir une à elle : aucun
          réglage ne gouverne cette liste — les jours sont ceux de l'événement, déclarés
          au back office. Sans `data-cfg` du tout elle sortirait de la trappe à CSS,
          qui ne s'ouvre que sur un bloc nommé ET porteur de réglages : le client ne
          pourrait plus rien changer à l'allure des jours. La carte qui s'ouvre est
          celle de « Étape — dates », ce qui est le bon nom pour ce bloc. */}
      <ul className="opts" data-cfg="datesTitle">
        {days.map((day) => {
          const on = ticked.includes(day.id);
          return (
            <li key={day.id}>
              <button
                type="button"
                className={`opt${on ? " on" : ""}`}
                aria-pressed={on}
                onClick={() => bascule(day.id)}
              >
                {/* ⚠️ UN CARRÉ, PARCE QU'ON EN COCHE PLUSIEURS. C'est la règle du
                    tunnel (voir `funnel.css`) : le rond dit « une seule réponse », le
                    carré « autant que vous voulez ». Cette étape portait le rond, donc
                    elle promettait un choix exclusif là où l'on retient trois jours.

                    ⚠️ ET PAS D'ICÔNE DE CALENDRIER : ce serait le MÊME dessin sur chaque
                    ligne, donc rien de distinctif — l'argument qui l'a déjà écarté de
                    l'étape typologie. La boîte reste dessinée dans les deux états,
                    faute de quoi la ligne sauterait à chaque clic. */}
                <span className="opt__c" aria-hidden="true">
                  {on && <Icon name="check" />}
                </span>
                <span className="opt__t">
                  <b>{longDay(day.day, locale)}</b>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* ⚠️ LE BOUTON GRISÉ DIT POURQUOI (bug #523). Zéro jour grise « Continuer » et
          « Enregistrer », et sans un mot le participant qui vient de tout décocher croit
          à une panne. Le modèle de l'étape typologie : une phrase qui ne vit que tant
          que rien n'est coché.

          ⚠️ SANS RENVOYER VERS « MA PARTICIPATION » pour qui ne vient plus du tout : le
          nom de cette page est réglable et l'étape peut être retirée du parcours, la
          phrase nommerait alors une page qui n'existe pas. */}
      {days.length > 1 && ticked.length === 0 && (
        <p className="hint">{t("TICK_AT_LEAST_ONE_DAY", "Cochez au moins un jour.")}</p>
      )}

      <SaveNote state={state} removal={removal} />

      {/**
       * ═══ DANS LE TUNNEL « CONTINUER », EN PAGE « ENREGISTRER », JAMAIS LES DEUX ═══
       *
       * ⚠️ PLUS D'« ENREGISTRER » DANS LE TUNNEL, MÊME EN CORRECTION (tâche #546). Qui
       * revenait corriger par le FIL D'ARIANE le trouvait à côté de « Continuer », sur les
       * quatre étapes qui savent écrire seules (participation, typologie, dates,
       * billetterie) : deux boutons pour avancer, dont l'un écrivait et sautait les
       * étapes restantes. Le parcours se PARCOURT, et la correction s'écrit à la
       * confirmation, comme la première fois.
       *
       * ⚠️ EN PAGE, IL RESTE, SEUL ET PRINCIPAL : il n'y a pas de suite où aller, et c'est
       * lui qui écrit (voir `funnel/stepSave.tsx`).
       *
       * Un seul primaire, le dernier de la rangée : c'est la règle de la confirmation.
       */}
      <div className="actions">
        {asPage && (
          <Button type="button" variant="ghost" onClick={() => go("/")}>
            {t("BACK_TO_HOME", "Retour à l'accueil")}
          </Button>
        )}
        {!asPage && previous && (
          <Button type="button" variant="ghost" onClick={() => go(previous.path)}>
            {t("BACK", "Retour")}
          </Button>
        )}
        {asPage && (
          <Button
            type="button"
            loading={state === "saving"}
            // La même règle que « Continuer » : on n'enregistre pas zéro jour.
            disabled={ticked.length === 0 || !ready}
            onClick={() => save({ days: ticked })}
          >
            {t("SAVE", "Enregistrer")}
          </Button>
        )}
        {!asPage && (
          <Button
            type="button"
            /* Au moins un jour, comme la V1 (`disabled={daysId.length < 1}`) : une
               inscription sans aucun jour n'en est pas une — le serveur en recalcule
               `isRegistered` à faux, et le participant se présenterait à la porte avec
               une réponse qui ne l'inscrit nulle part. */
            disabled={ticked.length === 0 || !next}
            onClick={() => next && go(next.path, { days: ticked })}
          >
            {t("CONTINUE", "Continuer")}
          </Button>
        )}
      </div>
    </section>
  );
}
